'use client';

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { fetchAboutMain } from '../api';
import priteshTeams from '../../assets/images/pritesh_teams.png';
import priteshInfo from '../../assets/images/pritesh_info.png';
import groupIcon1 from '../../assets/images/groupicon1.svg';
import groupIcon2 from '../../assets/images/group_icon2.svg';
import groupIcon4 from '../../assets/images/group_icon4.svg';
import shield from '../../assets/images/shield.svg';
import realEstateAgent from '../../assets/images/real-estate_agent.svg';
import team1 from '../../assets/images/teams/team1.png';
import team2 from '../../assets/images/teams/team2.png';
import team3 from '../../assets/images/teams/team3.png';
import team4 from '../../assets/images/teams/team4.png';
import team5 from '../../assets/images/teams/team5.png';
import { Link } from 'react-router-dom';
import MetaUpdater from '../metadata/MetaUpdater';

interface ListItem {
  en: string;
  ar: string;
  _id: string;
}

interface AboutMainData {
  _id: string;
  title: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  listItems: ListItem[];
  solvedCases: number;
  createdAt: string;
  updatedAt: string;
}

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1200 },
    items: 3,
  },
  desktop: {
    breakpoint: { max: 1200, min: 992 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 992, min: 768 },
    items: 3,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
  },
};

const AboutUs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [aboutData, setAboutData] = useState<AboutMainData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('what-we-do');

  useEffect(() => {
    const loadAboutData = async () => {
      try {
        const data = await fetchAboutMain();
        setAboutData(data);
        setLoading(false);
      } catch (err) {
        setError(t('error_message'));
        setLoading(false);
      }
    };
    loadAboutData();
  }, [t]);

  if (!aboutData) {
    return (
      <div
        className="container mx-auto px-4 text-center my-12"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="main-title">
          <h2 className="text-3xl md:text-4xl font-bold">
            {/* {t('no_data_message')} */}
          </h2>
        </div>
      </div>
    );
  }

  // Determine the current language
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';

  return (
    <>
      <MetaUpdater page="about" />
      <section
        className="about_image"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4 ">
          <div
            className="row flex justify-center"
            style={{ direction: t('language') === 'ar' ? 'rtl' : 'ltr' }}
          >
            <div className="col-md-12 text-center">
              <div className="main-title">
                <h2 className="text-3xl md:text-4xl font-bold">
                  {t('about_title')}
                </h2>
                <p className="text-lg">{t('about_subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="who-we-are py-12"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4">
          <div
            className="row flex flex-wrap"
            style={{
              direction: t('language') === 'ar' ? 'rtl' : 'ltr',
            }}
          >
            <div
              className="col-md-5 mb-8 md:mb-0"
              // style={{ padding: '0 50px 0 0' }}
            >
              <div
                className="main-title"
                style={{ textAlign: currentLang === 'ar' ? 'right' : 'left' }}
              >
                <h2 className="text-3xl md:text-4xl font-bold">
                  {t('who_we_are')}
                </h2>
                <h5 className="text-xl font-semibold">
                  {/* {aboutData.title[currentLang]} */}
                </h5>
                <p className="mt-4">{aboutData.description[currentLang]}</p>
                <ul
                  className={
                    t('language') === 'ar'
                      ? 'list-disc pr-5 mt-4'
                      : 'list-disc pl-5 mt-4'
                  }
                  style={{ direction: t('language') === 'ar' ? 'rtl' : 'ltr' }}
                >
                  {aboutData.listItems.map((item) => (
                    <li key={item._id}>{item[currentLang]}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-md-7">
              <div className="pritesh_teams_banners relative">
                <div className="team_details">
                  <img
                    src={priteshTeams}
                    className="img-fluid w-full rounded-lg"
                    alt={t('who_we_are')}
                  />
                </div>
                <div
                  className="total_statrics"
                  style={{
                    textAlign: t('language') === 'ar' ? 'right' : 'left',
                  }}
                >
                  <h4>{aboutData.solvedCases}+</h4>
                  <span>{t('solved_cases')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="meet-us py-12"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4">
          <div
            className="row flex flex-wrap"
            style={{ direction: t('language') === 'ar' ? 'rtl' : 'ltr' }}
          >
            <div className="col-md-5 mb-8 md:mb-0">
              <div className="team_pic">
                <img
                  src={priteshInfo}
                  className="img-fluid w-full rounded-lg"
                  alt={t('meet_dr_pritesh')}
                />
              </div>
            </div>

            <div className="col-md-7">
              <div className="about_info">
                <div
                  className="main-title"
                  style={{
                    textAlign: t('language') === 'ar' ? 'right' : 'left',
                  }}
                >
                  <h4 className="text-2xl font-bold">{t('meet_dr_pritesh')}</h4>
                  <p className="mt-2">{t('dr_pritesh_description')}</p>
                </div>

                <ul
                  className={`nav nav-pills mb-3 flex ${
                    t('language') === 'ar'
                      ? 'space-x-reverse space-x-4'
                      : 'space-x-4'
                  }`}
                  role="tablist"
                  style={{ direction: currentLang === 'ar' ? 'rtl' : 'ltr' }}
                >
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'what-we-do' ? 'active' : ''}`}
                      onClick={() => setActiveTab('what-we-do')}
                      type="button"
                    >
                      {t('tab_what_we_do')}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'our-mission' ? 'active' : ''}`}
                      onClick={() => setActiveTab('our-mission')}
                      type="button"
                    >
                      {t('tab_our_mission')}
                    </button>
                  </li>
                </ul>
                <div
                  className="tab-content"
                  style={{ direction: t('language') === 'ar' ? 'rtl' : 'ltr' }}
                >
                  {activeTab === 'what-we-do' && (
                    <div className="tab-pane fade show active" role="tabpanel">
                      <div className="rounded_boxes p-6 bg-gray-100 rounded-lg">
                        <div
                          className="half_wrappers grid grid-cols-1 md:grid-cols-2 gap-4"
                          style={{
                            direction: currentLang === 'ar' ? 'rtl' : 'ltr',
                          }}
                        >
                          <div
                            className="icon_text flex items-center"
                            style={{
                              flexDirection:
                                t('language') === 'ar' ? 'row-reverse' : 'row',
                            }}
                          >
                            <div
                              className="icon"
                              style={{
                                margin:
                                  t('language') === 'ar'
                                    ? '0 0 0 1rem'
                                    : '0 1rem 0 0',
                              }}
                            >
                              <img
                                src={groupIcon1}
                                alt={t('business_setup')}
                                style={{ width: '50px', height: '50px' }}
                              />
                            </div>
                            <div className="content">
                              <h6 className="font-semibold">
                                {t('business_setup')}
                              </h6>
                            </div>
                          </div>

                          <div
                            className="icon_text flex items-center"
                            style={{
                              flexDirection:
                                t('language') === 'ar' ? 'row-reverse' : 'row',
                            }}
                          >
                            <div
                              className="icon"
                              style={{
                                margin:
                                  t('language') === 'ar'
                                    ? '0 0 0 1rem'
                                    : '0 1rem 0 0',
                              }}
                            >
                              <img
                                src={groupIcon2}
                                alt={t('contract_drafting')}
                                style={{ width: '50px', height: '50px' }}
                              />
                            </div>
                            <div className="content">
                              <h6 className="font-semibold">
                                {t('contract_drafting')}
                              </h6>
                            </div>
                          </div>

                          <div
                            className="icon_text flex items-center"
                            style={{
                              flexDirection:
                                t('language') === 'ar' ? 'row-reverse' : 'row',
                            }}
                          >
                            <div
                              className="icon"
                              style={{
                                margin:
                                  t('language') === 'ar'
                                    ? '0 0 0 1rem'
                                    : '0 1rem 0 0',
                              }}
                            >
                              <img
                                src={groupIcon4}
                                alt={t('visa_services')}
                                style={{ width: '50px', height: '50px' }}
                              />
                            </div>
                            <div className="content">
                              <h6 className="font-semibold">
                                {t('visa_services')}
                              </h6>
                            </div>
                          </div>

                          <div
                            className="icon_text flex items-center"
                            style={{
                              flexDirection:
                                t('language') === 'ar' ? 'row-reverse' : 'row',
                            }}
                          >
                            <div
                              className="icon"
                              style={{
                                margin:
                                  t('language') === 'ar'
                                    ? '0 0 0 1rem'
                                    : '0 1rem 0 0',
                              }}
                            >
                              <img
                                src={shield}
                                alt={t('ip_protection')}
                                style={{ width: '50px', height: '50px' }}
                              />
                            </div>
                            <div className="content">
                              <h6 className="font-semibold">
                                {t('ip_protection')}
                              </h6>
                            </div>
                          </div>

                          <div
                            className="icon_text flex items-center"
                            style={{
                              flexDirection:
                                t('language') === 'ar' ? 'row-reverse' : 'row',
                            }}
                          >
                            <div
                              className="icon"
                              style={{
                                margin:
                                  t('language') === 'ar'
                                    ? '0 0 0 1rem'
                                    : '0 1rem 0 0',
                              }}
                            >
                              <img
                                src={realEstateAgent}
                                alt={t('real_estate_advisory')}
                                style={{ width: '50px', height: '50px' }}
                              />
                            </div>
                            <div className="content">
                              <h6 className="font-semibold">
                                {t('real_estate_advisory')}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'our-mission' && (
                    <div
                      className="tab-pane fade show active"
                      role="tabpanel"
                      style={{
                        direction: currentLang === 'ar' ? 'rtl' : 'ltr',
                      }}
                    >
                      <div className="rounded_boxes  p-6 bg-gray-100 rounded-lg">
                        <p>{t('our_mission_description')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="our-team"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div
                className="main-title"
                style={{ textAlign: t('language') === 'ar' ? 'right' : 'left' }}
              >
                <h2>{t('our_team_title')}</h2>
                <p>{t('our_team_description')}</p>
              </div>
            </div>
          </div>

          <div
            className="contain"
            style={{ direction: currentLang === 'ar' ? 'rtl' : 'ltr' }}
          >
            <Carousel
              responsive={responsive}
              autoPlay={true}
              arrows={false}
              autoPlaySpeed={3000}
              infinite={true}
              showDots={true}
              className="z-10 owl-carousel owl-theme"
              itemClass="mt-4 pb-5"
              rtl={t('language') === 'ar'} // Enable RTL for Arabic
            >
              <div className="item px-2">
                <div className="position-relative">
                  <img
                    src={team1}
                    className="img-fluid rounded-0 mx-auto d-block"
                    style={{
                      width: '400px',
                      height: '250px',
                      objectFit: 'cover',
                    }}
                    alt={t('our_team_title')}
                  />
                </div>
              </div>
              <div className="item px-2">
                <div className="position-relative">
                  <img
                    src={team2}
                    className="img-fluid rounded-0 mx-auto d-block"
                    style={{
                      width: '400px',
                      height: '250px',
                      objectFit: 'cover',
                    }}
                    alt={t('our_team_title')}
                  />
                </div>
              </div>
              <div className="item px-2">
                <div className="position-relative">
                  <img
                    src={team3}
                    className="img-fluid rounded-0 mx-auto d-block"
                    style={{
                      width: '400px',
                      height: '250px',
                      objectFit: 'cover',
                    }}
                    alt={t('our_team_title')}
                  />
                </div>
              </div>
              <div className="item px-2">
                <div className="position-relative">
                  <img
                    src={team4}
                    className="img-fluid rounded-0 mx-auto d-block"
                    style={{
                      width: '400px',
                      height: '250px',
                      objectFit: 'cover',
                    }}
                    alt={t('our_team_title')}
                  />
                </div>
              </div>
              <div className="item px-2">
                <div className="position-relative">
                  <img
                    src={team5}
                    className="img-fluid rounded-0 mx-auto d-block"
                    style={{
                      width: '400px',
                      height: '250px',
                      objectFit: 'cover',
                    }}
                    alt={t('our_team_title')}
                  />
                </div>
              </div>
            </Carousel>
          </div>
        </div>
      </section>

      <section
        className="newsletter_cta py-12"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4">
          <div
            className="row flex justify-center"
            style={{ direction: t('language') === 'ar' ? 'rtl' : 'ltr' }}
          >
            <div className="col-md-12 text-center">
              <div className="main-title">
                <h2 className="text-3xl md:text-4xl font-bold">
                  {t('newsletter_cta_title')}
                </h2>
                <p className="mt-4">{t('newsletter_cta_description')}</p>
                <Link
                  to="/appointment"
                  className="mt-4 fw-bold inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                  {t('book_consultation')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {currentLang === 'ar' && (
        <style>{`
    .who-we-are .main-title ul li::after {
      left: 100%;
    }
    .who-we-are .main-title ul li {
      left: -20px;
    }
      .meet-us .main-title h4 ,.meet-us .main-title p{
        text-align: end;}
  `}</style>
      )}
    </>
  );
};

export default AboutUs;
